/**
 * Roles y permisos del PMIS.
 * Extraido del PMIS_Tecpetrol.html (lineas 220-541).
 */

export const ROLES = [
  "GTE",
  "JEFE",
  "CFE",
  "PL",
  "PL2",
  "INT",
  "PLANNER",
  "ADOR",
  "ABAS",
  // Producción / Mantenimiento / Comisionamiento — incorporados para el flujo
  // de cierre de obra (entrega a Operaciones y firma del Punch List).
  // OPER y MTTO son roles internos de Tecpetrol (área Producción y área
  // Mantenimiento). COMM es la Autoridad de Comisionamiento: puede ser
  // asumido por un OPER o MTTO interno (vía `rolesSecundarios`) o por un
  // especialista externo (consultor de comm). COMM aprueba el listado de
  // pendientes cuando se usa la modalidad EXTENSA (Protocolo Comm).
  "OPER",
  "MTTO",
  "COMM",
  // ALMACEN — gestiona la entrega de materiales solicitados vía MR
  // (Material Requisition). Recibe la notificación cuando un PL2 emite
  // un MR contra un contrato marco de tipo MATERIALES y opera el flujo
  // de despacho/entrega/cierre. NO crea MRs, solo los procesa.
  "ALMACEN",
  "ADMIN_PMIS",
] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  GTE: "Gerente",
  JEFE: "Jefe",
  CFE: "Controller Facilities Engineer",
  PL: "Project Leader",
  PL2: "Project Leader 2",
  INT: "Interventor externo",
  PLANNER: "Programador del contratista",
  ADOR: "Administrador del contratista",
  ABAS: "Abastecimientos Tecpetrol",
  OPER: "Usuario Operaciones (Producción)",
  MTTO: "Usuario Mantenimiento",
  COMM: "Autoridad de Comisionamiento",
  ALMACEN: "Almacén — gestión de entregas",
  ADMIN_PMIS: "Administrador del PMIS",
};

/**
 * Permisos por acción. Alineados con la Declaración de Alcance VD §3.2 a §3.4.
 *
 * Notas clave de la VD:
 *   - ADOR = Administrador del Contratista. Personal del contratista con
 *     permisos limitados: ve sus PO/SO asignadas, genera RD, reporta eventos,
 *     propone presupuestos/PDT junto al PLANNER (no aprueba nada).
 *   - ADMIN_PMIS = Administrador del PMIS (sustituye al "Editor del PMIS" del
 *     VD). Acceso técnico completo; sus acciones quedan auditadas en log
 *     inmutable (VD §3.5).
 */
export const PERMS: Record<string, Partial<Record<Role, true>>> = {
  // Budget (informativo) — sólo personal Tecpetrol. Contratistas e INT NO acceden.
  // Los Budget NO se crean a mano: se auto-generan desde las fechas del AFE.
  "budget:view": { GTE: true, JEFE: true, PL: true, PL2: true, CFE: true, ABAS: true },

  // Catálogo de Costes (ampliable por Admin PMIS, JEFE y PL — ajuste piloto).
  // ADMIN_PMIS pasa por short-circuit en usePermissions; aquí se listan los
  // roles de negocio que pueden crear/editar clases de coste.
  "costes:view": { GTE: true, JEFE: true, PL: true, PL2: true, CFE: true, ABAS: true },
  "costes:manage": { JEFE: true, PL: true },

  // AFE - VD §3.2 ("PL propone, JEFE aprueba") — sólo personal Tecpetrol.
  // El scope (puedeEditarAfe) además restringe edit al PL/Back-PL del AFE.
  // OPER y MTTO necesitan ver los AFEs donde figuran como receptores de la
  // facilidad (firman el listado de pendientes al cierre). COMM ve para
  // el contexto del listado en modalidad extensa.
  "afe:view": {
    GTE: true,
    JEFE: true,
    PL: true,
    PL2: true,
    CFE: true,
    ABAS: true,
    OPER: true,
    MTTO: true,
    COMM: true,
  },
  "afe:create": { JEFE: true, PL: true, PL2: true, CFE: true },
  "afe:edit": { JEFE: true, PL: true, PL2: true, CFE: true },
  "afe:approve": { JEFE: true, GTE: true }, // JEFE aprueba; GTE si > 10%
  "afe:close": { JEFE: true },

  // PR / PO - VD §3.3 — sólo personal Tecpetrol; el scope filtra por AFE owner.
  "pr:view": { PL: true, PL2: true, ABAS: true, CFE: true, JEFE: true, GTE: true },
  "pr:create": { PL: true, PL2: true, CFE: true },
  "pr:edit": { ABAS: true, PL: true, PL2: true, CFE: true },
  "pr:approve": { PL: true, JEFE: true },
  /**
   * Edición de los campos de TIEMPOS de la PR/PO (Nº ET, fechas de
   * gestión, hitos del comprador). Antes vivía como hardcode en PrPage
   * / PoPage — ahora pasa por la matriz para que se pueda gobernar.
   */
  "pr:edit_tiempos": { PL: true, PL2: true, JEFE: true },

  "po:view": { PL: true, PL2: true, ABAS: true, CFE: true, JEFE: true, GTE: true },
  "po:create": { PL: true, PL2: true, CFE: true, ABAS: true },
  "po:edit": { ABAS: true, PL: true, PL2: true, CFE: true },
  "po:approve": { PL: true, PL2: true, JEFE: true },

  // PR Estadísticas / análisis histórico — visible para liderazgo y abastecimientos
  "pr:stats": {
    GTE: true,
    JEFE: true,
    CFE: true,
    PL: true,
    PL2: true,
    ABAS: true,
  },

  // PO Estadísticas / análisis histórico de entregas
  "po:stats": {
    GTE: true,
    JEFE: true,
    CFE: true,
    PL: true,
    PL2: true,
    ABAS: true,
  },

  // SO Estadísticas / análisis de cumplimiento por contratista
  "so:stats": {
    GTE: true,
    JEFE: true,
    CFE: true,
    PL: true,
    PL2: true,
    INT: true,
  },

  // Contratos Marco. El permiso ABRE el modulo; el ALCANCE lo decide
  // `scopeContratos`: el contratista solo ve los suyos y el INT los de las OT
  // que revisa. Sin ese filtro, dar la llave al contratista le mostraria los
  // contratos de sus competidores.
  "contract:view": {
    PL: true, PL2: true, ABAS: true, CFE: true, JEFE: true, GTE: true,
    INT: true, PLANNER: true, ADOR: true,
  },
  "contract:create": { JEFE: true, PL: true, PL2: true, CFE: true },
  "contract:edit": { JEFE: true, PL: true, PL2: true, CFE: true },

  // Registro de recursos — el contratista declara a diario lo que tiene en
  // campo y como lo reparte entre sus OT. NO se aprueba: la aprobacion vive
  // en el reporte diario de cada OT, que congela su porcion.
  "recursos:view": {
    GTE: true, JEFE: true, PL: true, PL2: true, CFE: true, INT: true,
    PLANNER: true, ADOR: true,
  },
  // Digita el contratista. El PL2 corrige cuando hace falta — por eso tiene
  // la llave, no porque le corresponda declarar.
  "recursos:edit": { PLANNER: true, ADOR: true, PL2: true },

  // SO - VD §3.3
  "so:view": { GTE: true, JEFE: true, PL: true, PL2: true, CFE: true, PLANNER: true, ADOR: true, INT: true, ABAS: true },
  "so:create": { PL: true, PL2: true, CFE: true },
  "so:edit": { PL: true, PL2: true, CFE: true },
  // Revisión del Interventor de la cotización (escala INT → PL2), igual que RD.
  "so:approve_int": { INT: true },
  "so:approve": { PL: true, PL2: true, JEFE: true, GTE: true },
  "so:request_quotation": { PL: true, PL2: true, CFE: true },
  "so:propose_budget": { PLANNER: true, ADOR: true }, // VD §3.3: contratista propone presupuesto/PDT
  "so:integrate_pdt": { PL: true, PL2: true },
  // Recursos APU (costos directos / AIU / márgenes) — información sensible.
  // Sólo personal Tecpetrol; el contratista (PLANNER/ADOR) e interventores
  // (INT) NO la ven. Configurable desde la matriz (fila so.ver_recursos_apu).
  "so:ver_recursos_apu": { GTE: true, JEFE: true, CFE: true, PL: true, PL2: true, ABAS: true },

  // RD - Reporte Diario - VD §3.4
  "rd:view": { GTE: true, JEFE: true, PL: true, PL2: true, CFE: true, INT: true, PLANNER: true, ADOR: true },
  "rd:create": { PLANNER: true, ADOR: true, INT: true }, // VD §3.4 - ADOR/PLANNER generan RD
  "rd:edit": { PLANNER: true, ADOR: true },
  "rd:approve_int": { INT: true },
  "rd:approve_pl2": { PL: true, PL2: true, CFE: true },
  "rd:integrate": { PL: true, PL2: true },

  // Eventos - todos los roles operacionales pueden reportar.
  // OPER/MTTO ven eventos para captura de lecciones aprendidas en la
  // entrega de obra. COMM ve eventos relacionados a comisionamiento.
  "event:view": {
    PL: true,
    PL2: true,
    JEFE: true,
    CFE: true,
    INT: true,
    PLANNER: true,
    ADOR: true,
    ABAS: true,
    GTE: true,
    OPER: true,
    MTTO: true,
    COMM: true,
  },
  "event:create": {
    PL: true,
    PL2: true,
    JEFE: true,
    CFE: true,
    INT: true,
    PLANNER: true,
    ADOR: true,
    GTE: true,
    OPER: true,
    MTTO: true,
    COMM: true,
  },

  // Actas - VD §3.3. OPER/MTTO ven actas de las obras que reciben para
  // verificación de alcance contra lo construido.
  "act:view": {
    GTE: true,
    JEFE: true,
    PL: true,
    PL2: true,
    CFE: true,
    INT: true,
    PLANNER: true,
    ADOR: true,
    ABAS: true,
    OPER: true,
    MTTO: true,
  },
  "act:generate": { PL: true, PL2: true, CFE: true, PLANNER: true, ADOR: true },
  // Firma del contratista sobre el acta de cantidades — el ADOR
  // (Administrador del Contratista) es quien firma formalmente; el PLANNER
  // tiene el respaldo para casos de back-up.
  "act:approve_cont": { ADOR: true, PLANNER: true },
  "act:approve_int": { INT: true },
  "act:approve_pl2": { PL: true, PL2: true },
  // Marcar el acta como FACTURADA (acción administrativa post-aprobación).
  "act:invoice": { PL: true, PL2: true, ADOR: true, JEFE: true },

  // Autoridades / Personal del PMIS
  // `users:view` — pueden VER el listado de personal y la matriz de permisos
  // en modo lectura: JEFE, GTE, CFE, PL, PL2, ABAS. Cualquier modificación
  // (crear usuario, cambiar rol, fijar clave, editar matriz) requiere
  // `users:manage`, que es exclusivo del ADMIN_PMIS (short-circuit en `can`).
  "users:view": {
    JEFE: true,
    PL: true,
    PL2: true,
    ABAS: true,
    GTE: true,
    CFE: true,
  },
  "users:manage": {}, // Solo ADMIN_PMIS vía short-circuit en `can()`

  // Gestión de reportes — qué PDFs/impresiones están autorizados a generarse.
  // EXCLUSIVA del ADMIN_PMIS. Si un usuario tiene ADMIN_PMIS como rol
  // secundario, debe haber ingresado eligiendo ese rol para acceder.
  "reportes:admin": {},

  // ── Mejoras PMIS (feedback / hallazgos del sistema) ────────────────
  // - `mejoras:view` y `mejoras:create`: TODOS los roles activos. Cualquier
  //   usuario que esté logueado puede reportar observaciones / hallazgos.
  // - `mejoras:responder`: solo ADMIN_PMIS (responde y cambia estado de
  //   ABIERTO → EN_PROCESO → CERRADO). Short-circuit en `can()` lo cubre.
  "mejoras:view": {
    GTE: true,
    JEFE: true,
    CFE: true,
    PL: true,
    PL2: true,
    INT: true,
    PLANNER: true,
    ADOR: true,
    ABAS: true,
    OPER: true,
    MTTO: true,
    COMM: true,
    ALMACEN: true,
  },
  "mejoras:create": {
    GTE: true,
    JEFE: true,
    CFE: true,
    PL: true,
    PL2: true,
    INT: true,
    PLANNER: true,
    ADOR: true,
    ABAS: true,
    OPER: true,
    MTTO: true,
    COMM: true,
    ALMACEN: true,
  },
  "mejoras:responder": {}, // solo ADMIN_PMIS vía short-circuit

  // ── Material Requisitions (MR) — pedidos contra Contrato Marco de
  //    MATERIALES. Es una "PO simplificada" sin PR de origen.
  //
  // - `mr:view`: visible a todos los roles de Tecpetrol que necesitan
  //   trazabilidad (PL2 originante, JEFE, GTE, CFE, ABAS, ALMACEN) y al
  //   contratista/admin del proveedor (PLANNER/ADOR de ese contrato).
  // - `mr:create`: SOLO PL2 (de la disciplina del contrato marco — el
  //   guard lo aplica `puedeCrearMrPara(contrato, user)` en `mrRules.ts`).
  // - `mr:edit`: PL2 creador mientras la MR esté en BORRADOR.
  // - `mr:gestionar`: ALMACEN — cambia estado EMITIDA → EN_ALMACEN
  //   → ENTREGADA y registra fecha de entrega real.
  // - `mr:pagar`: ABAS — registra el pago al proveedor (estado PAGADA).
  "mr:view": {
    GTE: true,
    JEFE: true,
    CFE: true,
    PL: true,
    PL2: true,
    ABAS: true,
    ALMACEN: true,
    PLANNER: true,
    ADOR: true,
  },
  "mr:create": { PL2: true },
  "mr:edit": { PL2: true },
  "mr:gestionar": { ALMACEN: true },
  "mr:pagar": { ABAS: true },
  // MR Estadísticas — análisis de tiempos de entrega y pago (mismo
  // patrón que `po:stats`). Incluye ALMACEN por su rol operativo.
  "mr:stats": {
    GTE: true,
    JEFE: true,
    CFE: true,
    PL: true,
    PL2: true,
    ABAS: true,
    ALMACEN: true,
  },

  // Configuración de notificaciones por correo — exclusiva de ADMIN_PMIS.
  // El short-circuit en `can()` permite acceso al ADMIN; nadie más entra al
  // módulo. Cubre tanto lectura como edición (no se separa view/edit por
  // ahora — quien lo abre, lo edita).
  "notif:admin": {},

  // Configuración de IA (proveedor activo + API keys) — exclusiva de
  // ADMIN_PMIS. Guarda credenciales sensibles (API keys de Claude / OpenAI /
  // Gemini / Copilot), por eso nadie más puede ver ni cambiarlas. El
  // ADMIN entra vía short-circuit en `can()`.
  "ia:admin": {},

  // Congelar el estado actual como escenario por defecto del PMIS
  // ("Establecer / Guardar como default"). Es herramienta de administración,
  // no función de negocio: barre tablas completas sin filtrar por contratista.
  // Antes cada pantalla la gateaba con su propio permiso de edición, y en el
  // módulo de Reportes Diarios eso significaba `rd:edit` = PLANNER + ADOR, es
  // decir, los contratistas. Objeto vacío ⇒ solo el ADMIN_PMIS por el
  // short-circuit de `can()`, pero delegable desde la Matriz de Autoridades.
  "pmis:snapshot_default": {},

  // Anular un acta emitida, o devolverla a borrador para corregirla. El PMIS
  // genera el acta pero NO factura: la facturación es externa, así que cuando
  // un acta ya aprobada se rechaza fuera del sistema hace falta una salida
  // dentro de él. El PL2 es quien la aprueba y quien recibe ese rechazo.
  // El ADMIN_PMIS entra por el short-circuit de `can()`; delegable a PL o CFE
  // desde la Matriz de Autoridades si la operación lo requiere.
  "act:delete": { PL2: true },

  // ── Gestión de Riesgos (PEP §4 — alineado al RACI del documento) ──
  //
  // Ver docs/ALCANCE_RIESGOS_STAKEHOLDERS.md sección 6.
  // El DRB (Decision Review Board) lo ejerce el GTE vía
  // `risk:approve` — decisión D6 del documento de alcance.
  //
  // El sentimiento IA de stakeholders queda gated en la UI a roles
  // PL/PL2 + JEFE (decisión D5) — no se modela como permiso aparte para
  // no inflar la matriz; se valida con `usePermissions().role`.
  "risk:view": {
    GTE: true,
    JEFE: true,
    PL: true,
    PL2: true,
    CFE: true,
    ABAS: true,
    INT: true,
    PLANNER: true,
    ADOR: true,
    OPER: true,
    MTTO: true,
  },
  "risk:create": {
    PL: true,
    PL2: true,
    CFE: true,
    INT: true,
    ABAS: true,
  },
  "risk:edit": {
    PL: true,
    PL2: true,
    CFE: true,
    ABAS: true,
  },
  // Aprobación de contingencias = función DRB. La asumen el GTE y
  // el JEFE (alternativa cuando el GTE no está). El ADMIN_PMIS entra por
  // short-circuit (rol técnico).
  "risk:approve": { GTE: true, JEFE: true },
  // Configurar catálogos (escalas, RBS, umbrales) — solo ADMIN_PMIS
  // por short-circuit.
  "risk:configure": {},

  // ── Gestión de Stakeholders (PMI Stakeholder Engagement) ──
  "stakeholder:view": {
    GTE: true,
    JEFE: true,
    PL: true,
    PL2: true,
    CFE: true,
    OPER: true,
    MTTO: true,
  },
  "stakeholder:edit": { PL: true, PL2: true, JEFE: true },
  "stakeholder:manage": { JEFE: true, GTE: true },

  // ── Comisionamiento (Punch List) ──
  //
  // El módulo controla los pendientes técnicos pre-cierre. Lo gestionan
  // el PL/PL2 con apoyo del CFE y los Interventores (que detectan
  // hallazgos en obra). Los contratistas (PLANNER/ADOR) pueden CONSULTAR
  // los pendientes de sus SOs para gestionar el cierre, pero no crean
  // ni cierran formalmente — eso queda en Tecpetrol + interventoría.
  "comm:view": {
    GTE: true,
    JEFE: true,
    PL: true,
    PL2: true,
    CFE: true,
    INT: true,
    PLANNER: true,
    ADOR: true,
    OPER: true,
    MTTO: true,
    COMM: true,
  },
  "comm:create": { PL: true, PL2: true, CFE: true, INT: true, COMM: true },
  "comm:edit": { PL: true, PL2: true, CFE: true, INT: true, COMM: true },
  /** Cierre formal de un pendiente — requiere validación FAC/INT. */
  "comm:cerrar": { PL: true, PL2: true, JEFE: true, INT: true, COMM: true },
  /**
   * Firma del listado de pendientes por Producción (Operaciones recibe la
   * obra). El listado no se puede cerrar formalmente sin la firma de OPER.
   */
  "comm:firma-operaciones": { OPER: true },
  /** Firma por Mantenimiento (custodio del activo en operación). */
  "comm:firma-mantenimiento": { MTTO: true },
  /**
   * Aprobación de un listado en modalidad POR_PROTOCOLO_COMM (extenso).
   * Solo COMM (interno o externo). Para la modalidad POR_AFE simple esta
   * firma no es requerida — basta con OPER + MTTO.
   */
  "comm:firma-comm": { COMM: true },
};

/**
 * Verifica si un rol tiene un permiso.
 *
 * El Administrador del PMIS (rol ADMIN_PMIS) tiene acceso completo a todas
 * las funciones del sistema. Sus acciones quedan registradas en log inmutable
 * (VD §3.5, §9.4). Reemplaza al "Editor del PMIS" del documento VD.
 *
 * El rol ADOR (Administrador del Contratista) opera con permisos limitados
 * según VD §3.2-§3.4: ve sus PO/SO asignadas, genera RD, reporta eventos y
 * propone presupuestos/PDT junto con el PLANNER. NO crea ni aprueba AFE,
 * PR, PO ni SO.
 */
export function can(role: Role | null, perm: string): boolean {
  if (!role) return false;
  if (role === "ADMIN_PMIS") return true;
  return Boolean(PERMS[perm]?.[role]);
}
